import { useEffect, useState, useCallback } from "react";
import { AlertTriangle } from "lucide-react";
import { api, fmtBytes } from "@/lib/api";

const LEVEL = {
  ok: { color: "#5d9a35", label: "Healthy" },
  warning: { color: "#EAB308", label: "70% reached — plan ahead" },
  high: { color: "#F97316", label: "85% reached — storage is getting full" },
  critical: { color: "#EF4444", label: "95% reached — uploads will soon be blocked" },
};

export const useStorage = () => {
  const [s, setS] = useState(null);
  const load = useCallback(() => api.get("/admin/storage").then((r) => setS(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  return [s, load];
};

export const StorageBanner = ({ s }) => {
  if (!s || s.level === "ok") return null;
  const L = LEVEL[s.level];
  return (
    <div data-testid="storage-warning-banner" className="mb-6 flex items-start gap-3 border px-4 py-3 text-sm" style={{ borderColor: L.color, background: `${L.color}1a`, color: "#fff" }}>
      <AlertTriangle size={18} style={{ color: L.color }} className="mt-0.5 shrink-0" />
      <div><strong>Storage {s.percent}% used.</strong> {L.label}. {fmtBytes(s.remaining_bytes)} remaining.</div>
    </div>
  );
};

export const StorageGauge = ({ s }) => {
  if (!s) return <div className="h-40 animate-pulse bg-[#111]" />;
  const L = LEVEL[s.level];
  const pct = Math.min(100, s.percent);
  return (
    <div className="border border-white/10 bg-[#111] p-6" data-testid="admin-storage-gauge">
      <div className="flex items-baseline justify-between">
        <span className="kicker">Storage capacity</span>
        <span className="font-mono text-xs" style={{ color: L.color }} data-testid="storage-level">{L.label}</span>
      </div>
      <div className="mt-4 flex items-end gap-3">
        <span className="font-display text-5xl font-bold text-white" data-testid="storage-percent">{s.percent.toFixed(1)}%</span>
        <span className="pb-2 font-mono text-xs text-white/60">{fmtBytes(s.used_bytes)} / {fmtBytes(s.quota_bytes)}</span>
      </div>
      <div className="relative mt-4 h-2 w-full bg-white/10">
        <div className="h-full transition-[width] duration-700" style={{ width: `${pct}%`, background: L.color }} />
        {[70, 85, 95].map((m) => <span key={m} className="absolute top-[-4px] h-4 w-px bg-white/40" style={{ left: `${m}%` }} title={`${m}%`} />)}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3 font-mono text-[11px] text-white/65">
        <div><div className="text-white/45">Remaining</div><div className="text-white" data-testid="storage-remaining">{fmtBytes(s.remaining_bytes)}</div></div>
        <div><div className="text-white/45">Live photos</div><div className="text-white">{s.photos} · {fmtBytes(s.active_bytes)}</div></div>
        <div><div className="text-white/45">Unrecoverable</div><div className="text-white" title="Files of deleted/replaced photos — still occupy storage">{fmtBytes(s.orphaned_bytes)}</div></div>
      </div>
      <p className="mt-4 text-[12px] leading-relaxed text-white/55">{s.note}</p>
    </div>
  );
};
